import { keepPreviousData, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import clsx from "clsx";
import {
  CalendarClock,
  Clock3,
  Eye,
  History,
  PanelRightClose,
  PanelRightOpen,
  Pause,
  Play,
  Plus,
  RotateCcw,
  Users,
  Zap,
  ZapOff,
} from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

import { api, background, changedElsewhere } from "../../api/client";
import type {
  Direx,
  DirexIncident,
  DirexRole,
  DirexStatus,
  IncidentChanges,
  IncidentKind,
  NewIncident,
  Phase,
} from "../../api/types";
import { Banner } from "../../components/Callout";
import { EmptyState } from "../../components/EmptyState";
import { IncidentCode } from "../../components/IncidentCode";
import { PanelNote } from "../../components/panel";
import { motion, Motion, Presence } from "../../components/presence";
import { SaveDossierHint } from "../../components/SaveDossierHint";
import { Select } from "../../components/Select";
import { SidePanel } from "../../components/SidePanel";
import { Badge, Button, IconButton, inputClass, inputSmClass, SearchField, SectionLabel, Segmented, Tabs } from "../../components/ui";
import { useMediaQuery } from "../../components/useMediaQuery";
import { ViewHeader } from "../../components/ViewHeader";
import { unitDesignation } from "../../i18n/designation";
import { locale, t, tServer } from "../../i18n/i18n";
import { term } from "../../i18n/vocabulary";
import { formatDtg } from "../../map/dtg";
import { effectiveOpo } from "../../map/units/useDeployment";
import { plural } from "../../plural";
import { licenceStore } from "../../state/licenceScope";
import { useUi } from "../../state/ui";
import { clearLocalCopy, leaveWith, readLocalCopy, useUnsavedDraft } from "../../state/unsavedDrafts";
import { DossierEmptyState } from "../DossierEmptyState";
import { OpoSelector } from "../order/OrderView";
import { dtgRange, KIND_COLOR, KIND_LABEL, roleLabel, STATUS_PLURAL, terminalName, UNASSIGNED } from "./common";
import { SourcesNote } from "../sketch/SketchBase";
import { DirexSketch } from "./DirexSketch";
import { GenerateMenu } from "./GenerateMenu";
import { IncidentPanel, incidentChanges, incidentEdited, StatusIcon } from "./IncidentPanel";
import { NewIncidentDialog } from "./NewIncidentDialog";
import { type ProposalDecision, ProposalsBand } from "./ProposalsBand";
import { RoleMenu } from "./RoleMenu";
import { PrivateDirexBanner } from "../../sharing/PrivateDirexBanner";
import { SituationPanel } from "./SituationPanel";
import { zoneRefusalOf } from "../../map/zones/zoneRefusal";
import { ZoneWaitState } from "../../map/zones/ZoneWaitCallout";

/** DIREX (vue « DIREX ») : direction de l'exercice — horloge d'exercice partagée, chronologie T0 à T3 et ses
 *  GDH, tableau des incidents MEL/MIL par cible et par temps (l'écran Direx de l'iPad), fiche de l'incident,
 *  croquis de situation et journal. Tous les postes du dossier voient les mêmes incidents, statuts et horloge
 *  (actualisation toutes les 5 s). Chaque incident a son heure d'injection : l'horloge l'injecte d'elle-même
 *  quand l'injection automatique est active. Chaque poste a un rôle : animateur (tout), cellule (incidents
 *  injectés, traite les siens), observateur (consultation). */

const TEMPOS: Phase[] = ["T1", "T2", "T3"];
const SPEEDS = [1, 2, 5, 10, 30, 60];
type Side = "incident" | "croquis" | "journal";
type ClockAction = "start" | "pause" | "reset" | "speed" | "auto_inject";

function useExerciseNow(data: Direx | undefined, fetchedAt: number): number | null {
  const [, setTick] = useState(0);
  const running = Boolean(data?.clock.running);
  useEffect(() => {
    if (!running) return;
    const id = window.setInterval(() => setTick((t) => t + 1), 1000);
    return () => window.clearInterval(id);
  }, [running]);
  if (!data?.clock.now) return null;
  const base = Date.parse(data.clock.now);
  return data.clock.running ? base + (Date.now() - fetchedAt) * data.clock.speed : base;
}

/** Horloge d'exercice, dans la bande de chronologie : l'animateur la démarre, l'arrête, en règle la vitesse, la
 *  remet au début et suspend l'injection automatique ; les autres postes la lisent. */
function Clock({
  data,
  now,
  busy,
  onAction,
}: {
  data: Direx;
  now: number | null;
  busy: boolean;
  onAction: (action: ClockAction, value?: number | boolean) => void;
}) {
  const setView = useUi((s) => s.setView);
  const animator = data.me.role === "animateur";
  if (!data.start || now === null) {
    if (!animator) return null;
    return (
      <Button variant="link" onClick={() => setView("mission")} title={t("L'horloge d'exercice part du début de l'exercice")}>
        {t("Renseigner le début de l'exercice")}
      </Button>
    );
  }
  const running = data.clock.running;
  const auto = data.clock.auto_inject;
  if (!animator) {
    return (
      <div className="flex h-8 items-center gap-2"
        title={running ? t("Heure d'exercice (GDH) : l'horloge tourne, réglée par l'animateur") : t("Heure d'exercice (GDH) : horloge arrêtée")}>
        <span className={clsx("size-2 rounded-full transition-colors", running ? "bg-ok" : "bg-line-strong")} />
        <span className={clsx("font-mono text-ui font-semibold tabular-nums", running ? "text-ink" : "text-ink-soft")}>
          {formatDtg(new Date(now).toISOString(), 0, data.time_zone)}
        </span>
        {data.clock.speed !== 1 && <span className="text-meta text-ink-muted tabular-nums">× {data.clock.speed}</span>}
      </div>
    );
  }
  return (
    <div className="flex items-center gap-1.5">
      <IconButton
        size="sm"
        variant={running ? "ghost" : "outline"}
        onClick={() => onAction(running ? "pause" : "start")}
        disabled={busy}
        title={running ? t("Arrêter l'horloge d'exercice") : t("Démarrer l'horloge d'exercice")}
        aria-label={running ? t("Arrêter l'horloge d'exercice") : t("Démarrer l'horloge d'exercice")}
      >
        {running ? <Pause size={14} /> : <Play size={14} />}
      </IconButton>
      <span className={clsx("min-w-[118px] text-center font-mono text-ui font-semibold tabular-nums", running ? "text-ink" : "text-ink-soft")}
        title={t("Heure d'exercice (GDH)")}>
        {formatDtg(new Date(now).toISOString(), 0, data.time_zone)}
      </span>
      <span className="w-[72px] shrink-0">
      <Select
        className={inputSmClass}
        value={String(data.clock.speed)}
        onChange={(e) => onAction("speed", Number(e.target.value))}
        aria-label={t("Vitesse de l'horloge")}
        title={t("Vitesse de l'horloge d'exercice")}
      >
        {SPEEDS.map((s) => (
          <option key={s} value={String(s)}>× {s}</option>
        ))}
      </Select>
      </span>
      <IconButton size="sm" onClick={() => onAction("reset")} disabled={busy} title={t("Revenir au début de l'exercice")}
        aria-label={t("Revenir au début de l'exercice")}>
        <RotateCcw size={14} />
      </IconButton>
      <IconButton
        size="sm"
        active={auto}
        onClick={() => onAction("auto_inject", !auto)}
        disabled={busy}
        aria-pressed={auto}
        aria-label={t("Injection automatique")}
        title={
          auto
            ? t("Injection automatique : chaque incident prévu part à son heure (cliquer pour suspendre)")
            : t("Injection automatique suspendue : les incidents s'injectent à la main (cliquer pour la reprendre)")
        }
      >
        {auto ? <Zap size={14} className="text-accent" /> : <ZapOff size={14} />}
      </IconButton>
    </div>
  );
}

/** Bande de chronologie sous l'en-tête : horloge d'exercice, puis les temps T0 à T3 avec leurs GDH, le temps en
 *  cours et le décompte des incidents. Les GDH passent à la ligne plutôt que d'être coupés. Fenêtre étroite (sous
 *  1 024 px) : l'horloge occupe une rangée, les quatre temps la suivante, sur toute la largeur. */
function Timeline({
  data,
  now,
  counts,
  clock,
}: {
  data: Direx;
  now: number | null;
  counts: Record<Phase, Record<DirexStatus, number>>;
  clock: ReactNode;
}) {
  const planned = data.me.role !== "cellule"; // une cellule ne connaît pas les incidents à venir
  const start = data.start ? Date.parse(data.start) : null;
  const elapsed = start !== null && now !== null ? (now - start) / 3_600_000 : null;
  return (
    <div className="flex shrink-0 flex-wrap border-b border-line bg-surface">
      <div className="flex shrink-0 items-center border-r border-line px-3 py-1.5 empty:hidden max-lg:w-full max-lg:border-r-0 max-lg:border-b">
        {clock}
      </div>
      <div className="grid min-w-0 flex-1 grid-cols-4 max-lg:basis-full">
        {data.phases.map((p) => {
          const current = elapsed !== null && elapsed >= p.start_h && elapsed < p.end_h;
          const done = elapsed !== null && elapsed >= p.end_h;
          const progress = current && elapsed !== null ? (elapsed - p.start_h) / (p.end_h - p.start_h) : done ? 1 : 0;
          const c = p.phase === "T0" ? null : counts[p.phase];
          return (
            <div key={p.phase} className={clsx("relative min-w-0 border-r border-line px-4 py-2 last:border-r-0", current && "bg-accent-soft")}>
              <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
                <span className={clsx("font-mono text-ui font-semibold", current ? "text-accent" : done ? "text-ink-muted" : "text-ink")}>
                  {p.phase}
                </span>
                {current && (
                  <Badge size="sm" tone="accent">
                    {t("En cours")}
                  </Badge>
                )}
                {c && (
                  <span className="ml-auto flex gap-1.5 text-meta text-ink-muted tabular-nums"
                    title={planned ? t("Prévus · injectés · traités") : t("Injectés · traités")}>
                    {planned && <><span>{c.prevu}</span>·</>}
                    <span className="text-warn">{c.injecte}</span>·<span className="text-ok">{c.traite}</span>
                  </span>
                )}
              </div>
              <div className="mt-0.5 flex flex-wrap gap-x-1 font-mono text-meta leading-snug text-ink-soft tabular-nums">
                {p.start_dtg ? (
                  dtgRange(p.start_dtg, p.end_dtg ?? "").map((part) => (
                    <span key={part} className="whitespace-nowrap">
                      {part}
                    </span>
                  ))
                ) : (
                  <span className="whitespace-nowrap">{`H${p.start_h >= 0 ? "+" : ""}${p.start_h} → H+${p.end_h}`}</span>
                )}
              </div>
              <div className="absolute inset-x-0 bottom-0 h-0.5 bg-line">
                <div className="h-full bg-accent transition-[width] duration-(--gx-enter)" style={{ width: `${progress * 100}%` }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/** Puce d'un incident dans la matrice : barre de couleur du type à gauche ; première ligne avec le code, le GDH
 *  d'injection prévu et l'icône de statut ; dessous, le titre sur toute la largeur (deux lignes au plus, césure
 *  dans la langue du dossier : jamais de mot coupé sans tiret). */
function IncidentChip({
  incident,
  selected,
  own,
  language,
  onClick,
}: {
  incident: DirexIncident;
  selected: boolean;
  own: boolean;
  language: string;
  onClick: () => void;
}) {
  const color = KIND_COLOR[incident.type];
  const planned = incident.status === "prevu" && incident.planned_dtg ? incident.planned_dtg.split(" ")[0] : null;
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={selected}
      title={`${incident.code} — ${incident.short}${planned ? ` ${t("(injection prévue {dtg})", { dtg: incident.planned_dtg })}` : ""}`}
      className={clsx(
        "flex w-full items-stretch gap-2 rounded-md border py-1.5 pr-2 pl-1 text-left transition-colors",
        motion("fade"),
        selected
          ? "border-accent bg-accent-soft"
          : own && incident.status === "injecte"
            ? "border-accent/50 bg-surface hover:bg-hover"
            : "border-line bg-surface hover:bg-hover",
        incident.status === "traite" && !selected && "opacity-60",
      )}
    >
      <span className="w-[3px] shrink-0 self-stretch rounded-full" style={{ background: color.fill }} />
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1.5">
          <IncidentCode kind={incident.type} code={incident.code} />
          {planned && <span className="font-mono text-meta text-ink-muted tabular-nums">{planned}</span>}
          {(incident.unit_absent || incident.unit_state) && (
            <Badge dot size="sm" tone="warn">
              {incident.unit_state === "unplaced" ? t("Cible à placer") : t("Cible retirée")}
            </Badge>
          )}
          {incident.annotation?.orphan && (
            <Badge dot size="sm" tone="warn">
              {t("Sans annotation")}
            </Badge>
          )}
          <span className="ml-auto flex shrink-0">
            <StatusIcon status={incident.status} size={12} />
          </span>
        </span>
        <span
          lang={language}
          className={clsx(
            "mt-1 line-clamp-2 text-meta leading-snug break-words hyphens-auto text-ink-soft",
            incident.status === "traite" && "line-through",
          )}
        >
          {incident.short}
        </span>
      </span>
    </button>
  );
}

/** Phrase traduite dont la partie entre <b> et </b> est en gras (le traducteur place le gras). */
function Bold({ text }: { text: string }) {
  const [before, strong, after] = text.split(/<\/?b>/);
  return (
    <>
      {before}
      <strong>{strong}</strong>
      {after}
    </>
  );
}

/** Copie locale d'une fiche d'incident modifiée : `generex-incident-draft:<dossier>:<OPO>:<incident>`. */
const INCIDENT_DRAFT_PREFIX = "generex-incident-draft:";

export function DirexView() {
  const exerciseId = useUi((s) => s.exerciseId);
  const params = useUi((s) => s.deploymentParams);
  const notify = useUi((s) => s.notify);
  const mapOpo = useUi((s) => s.opo);
  const setOpo = useUi((s) => s.setOpo);
  const queryClient = useQueryClient();
  const { data: exercises = [] } = useQuery({ queryKey: ["exercises"], queryFn: api.exercises });
  const exercise = exercises.find((e) => e.id === exerciseId);
  const complete = Boolean(exercise?.map_slug && exercise.parameters.level && exercise.parameters.odb);
  const centac = exercise?.parameters.level === "CENTAC";
  const opo = centac ? effectiveOpo(params, mapOpo) : undefined;
  const unsaved = Boolean(exerciseId && params && params.exercise_id !== exerciseId);
  const key = ["direx", exerciseId, opo ?? 1] as const;
  const query = useQuery({
    queryKey: key,
    queryFn: () => background(() => api.direx(exerciseId as string, opo)),
    enabled: Boolean(exerciseId && complete),
    refetchInterval: 5_000,
    placeholderData: keepPreviousData,
    retry: false,
  });
  const data = query.data;
  const now = useExerciseNow(data, query.dataUpdatedAt || Date.now());

  const [mode, setMode] = useState<"incidents" | "situation">("incidents");
  const [selected, setSelected] = useState<string | null>(null);
  const [side, setSide] = useState<Side>("journal");
  // Volet Incident, Croquis, Journal : ouvert d'emblée sur grand écran ; sur tablette (sous 1280 px), il s'ouvre
  // à la demande, en surimpression sur la matrice (SidePanel).
  const wide = useMediaQuery("(min-width: 1280px)");
  const [sideOpen, setSideOpen] = useState(() => window.matchMedia("(min-width: 1280px)").matches);
  // Retour au grand écran (fenêtre agrandie, iPad tourné) : le volet reprend sa place dans la vue.
  useEffect(() => {
    if (wide) setSideOpen(true);
  }, [wide]);
  const [sketchPhase, setSketchPhase] = useState<Phase>("T1");
  const [kind, setKind] = useState<IncidentKind | "all">("all");
  const [cellule, setCellule] = useState("");
  const [status, setStatus] = useState<DirexStatus | "all">("all");
  const [search, setSearch] = useState("");
  const [generateOpen, setGenerateOpen] = useState(false);
  const [creating, setCreating] = useState(false);

  const apply = (next: Direx) => queryClient.setQueryData(key, next);
  const onError = (e: Error) => {
    // Incident modifié entre-temps par un autre poste : rien n'est écrasé, la saisie reste, la DIREX est relue.
    if (changedElsewhere(e)) {
      notify({ text: t("Modifié par un autre poste"), detail: e.message, tone: "warn" });
      void queryClient.invalidateQueries({ queryKey: key });
      return;
    }
    notify({ text: t("Action impossible"), detail: e.message, tone: "error" });
  };
  // Incidents dont ce poste vient de changer le statut : pas d'avis pour ce qu'il a fait lui-même.
  const ownChanges = useRef(new Set<string>());
  const seen = useRef<{ key: string; injected: Set<string> } | null>(null);
  const mutation = useMutation({
    mutationFn: (run: () => Promise<Direx>) => run(),
    onSuccess: apply,
    onError,
  });
  const busy = mutation.isPending;
  const run = (fn: () => Promise<Direx>, done?: (next: Direx) => void) =>
    mutation.mutate(fn, { onSuccess: (next) => done?.(next) });

  const incidents = data?.incidents ?? [];
  const current = incidents.find((i) => i.id === selected) ?? null;
  // Brouillon de modification de l'incident choisi : tenu ici pour survivre à la fermeture du volet et au
  // passage par les onglets Croquis et Journal ; abandonné quand on choisit un autre incident.
  const [incidentDraft, setIncidentDraft] = useState<DirexIncident | null>(null);
  // Un autre incident choisi (ou celui-ci disparu) : la fiche de l'incident quitté est abandonnée — la question
  // commune l'a demandé avant (`select`) s'il y avait une saisie ; une fiche rétablie pour cet incident reste.
  useEffect(() => setIncidentDraft((d) => (d && d.id === current?.id ? d : null)), [current?.id]);
  // Fiche modifiée et non enregistrée : déclarée au registre des saisies (audit de persistance M4), copiée sur le
  // poste sous la licence, rétablie à la prochaine ouverture de la DIREX.
  const incidentDirty = incidentEdited(incidentDraft, current);
  const incidentCopyPrefix = `${INCIDENT_DRAFT_PREFIX}${exerciseId ?? ""}:${opo ?? 1}:`;
  const saveIncidentDraft = () =>
    new Promise<void>((resolve, reject) => {
      if (!current || !incidentDraft) return resolve();
      mutation.mutate(() => api.updateIncident(exerciseId as string, current.id, incidentChanges(incidentDraft, current), opo), {
        onSuccess: () => {
          setIncidentDraft(null);
          resolve();
        },
        onError: reject,
      });
    });
  useUnsavedDraft(incidentDraft && exerciseId ? `incident:${incidentCopyPrefix}${incidentDraft.id}` : null, incidentDirty, {
    label: t("Incident {code}", { code: current?.code ?? "" }),
    save: saveIncidentDraft,
    discard: () => setIncidentDraft(null),
    reveal: () => {
      setSide("incident");
      setSideOpen(true);
    },
    copy: incidentDraft ? { key: `${incidentCopyPrefix}${incidentDraft.id}`, value: incidentDraft, base: incidentDraft.version } : undefined,
    keep: true,
  });
  // Fiche d'une session précédente (onglet fermé, licence perdue) : l'incident est rouvert avec elle, une fois.
  const restoredFor = useRef<string | null>(null);
  useEffect(() => {
    if (!data || !exerciseId || restoredFor.current === incidentCopyPrefix) return;
    restoredFor.current = incidentCopyPrefix;
    for (const key of licenceStore.keys()) {
      if (!key.startsWith(incidentCopyPrefix)) continue;
      const copy = readLocalCopy<DirexIncident>(key);
      const target = data.incidents.find((i) => i.id === key.slice(incidentCopyPrefix.length));
      if (!copy || !target || !incidentEdited(copy.value, target)) {
        clearLocalCopy(key);
        continue;
      }
      setSelected(target.id);
      setSketchPhase(target.tempo);
      setSide("incident");
      setSideOpen(true);
      setIncidentDraft(copy.value);
      notify({
        text: t("Saisie retrouvée"),
        detail: t("La fiche de l'incident {code}, non enregistrée, est rétablie : enregistrez-la ou annulez-la.", { code: target.code }),
        tone: "info",
      });
      break;
    }
  }, [data, exerciseId, incidentCopyPrefix]);
  const me = data?.me;
  const animator = !me || me.role === "animateur";
  const trained = me?.role === "cellule";
  const counts = useMemo(() => {
    const out = Object.fromEntries(TEMPOS.map((t) => [t, { prevu: 0, injecte: 0, traite: 0 }])) as Record<Phase, Record<DirexStatus, number>>;
    for (const i of incidents) out[i.tempo][i.status] += 1;
    return out;
  }, [incidents]);
  const needle = search.trim().toLowerCase();
  const visible = incidents.filter(
    (i) =>
      (kind === "all" || i.type === kind) &&
      (!cellule || i.cellule === cellule) &&
      (status === "all" || i.status === status) &&
      (!needle || `${i.code} ${i.short} ${i.detail}`.toLowerCase().includes(needle)),
  );
  const rows = data
    ? [...data.targets.map((target) => ({
        key: target.key,
        label: target.commander ? t("{label} (CO)", { label: target.label }) : target.label,
        commander: target.commander,
      })),
       { key: UNASSIGNED, label: t("Éventuel"), commander: false }]
    : [];
  // Choisir un autre incident pendant la modification d'une fiche : la question commune d'abord (M4).
  const select = (incident: DirexIncident) => {
    const open = () => {
      setSelected(incident.id);
      setSketchPhase(incident.tempo);
      setSide("incident");
      setSideOpen(true);
    };
    if (incident.id !== current?.id) leaveWith(open);
    else open();
  };
  // Chaque modification part avec la version de l'incident lue (celle du brouillon pour la fiche) ; `done` ne
  // s'exécute qu'une fois le serveur d'accord : un refus garde le brouillon de la fiche.
  const change = (changes: IncidentChanges, done?: () => void) => {
    if (!current) return;
    if (changes.status) ownChanges.current.add(current.id);
    const body = { version: current.version, ...changes };
    run(() => api.updateIncident(exerciseId as string, current.id, body, opo), done && (() => done()));
  };
  // Incident choisi dans la liste des objets de la mission : sélectionné dès que la DIREX est chargée.
  const direxFocus = useUi((s) => s.direxFocus);
  const focusIncident = useUi((s) => s.focusIncident);
  useEffect(() => {
    if (!direxFocus || !data) return;
    const target = data.incidents.find((i) => i.id === direxFocus);
    if (target) {
      setMode("incidents");
      select(target);
    }
    focusIncident(null);
  }, [direxFocus, data]);

  const chooseRole = (role: DirexRole, cell: string | null) =>
    run(() => api.direxRole(exerciseId as string, role, cell, opo), (next) => {
      setCellule(next.me.role === "cellule" ? (next.me.cellule ?? "") : "");
      setStatus("all");
      if (!next.incidents.some((i) => i.id === selected)) setSelected(null);
      void queryClient.invalidateQueries({ queryKey: ["situation", exerciseId] });
      void queryClient.invalidateQueries({ queryKey: ["aar", exerciseId] });
      notify({
        text: t("Ce poste est désormais : {role}", {
          role:
            next.me.role === "cellule"
              ? t("cellule {cellule}", { cellule: term(next.me.cellule) })
              : roleLabel(next.me.role, next.me.label).toLowerCase(),
        }),
        tone: "ok",
      });
    });

  // Avis à chaque incident injecté par un autre poste ou par l'horloge : l'animateur voit partir les injections
  // automatiques, une cellule découvre les incidents qui lui arrivent.
  useEffect(() => {
    if (!data) return;
    const k = `${exerciseId}-${opo ?? 1}-${data.me.role}-${data.me.cellule ?? ""}`;
    const injected = new Set(data.incidents.filter((i) => i.status !== "prevu").map((i) => i.id));
    const before = seen.current;
    seen.current = { key: k, injected };
    const own = ownChanges.current;
    ownChanges.current = new Set();
    if (!before || before.key !== k) return;
    const fresh = data.incidents.filter((i) => i.status === "injecte" && !before.injected.has(i.id) && !own.has(i.id));
    if (fresh.length === 0) return;
    const mine = data.me.role === "cellule" ? fresh.filter((i) => i.cellule === data.me.cellule) : [];
    const first = (mine[0] ?? fresh[0])!;
    const count = plural(fresh.length, t("incident injecté"), t("incidents injectés"));
    notify({
      text:
        fresh.length === 1
          ? mine.length
            ? t("Incident pour votre cellule : {code}", { code: first.code })
            : t("Incident injecté : {code}", { code: first.code })
          : mine.length
            ? t("{incidents}, dont {n} pour votre cellule", { incidents: count, n: mine.length })
            : count,
      detail: fresh.length === 1 ? first.short : fresh.map((i) => i.code).join(", "),
      tone: mine.length ? "warn" : "info",
      action: { label: t("Voir"), run: () => select(first) },
    });
  }, [data]);

  const language = exercise?.language === "en" ? "en" : "fr";
  // Incidents proposés par les événements posés sur la carte (docs/08 §19) : retenus ou écartés par l'animateur.
  const decide = (incident: DirexIncident, decision: ProposalDecision) =>
    run(() => api.decideProposal(exerciseId as string, incident.id, decision, incident.version, opo), (next) => {
      if (decision !== "keep") return;
      const kept = next.incidents.find((i) => i.id === incident.id);
      notify({
        text: t("Incident retenu : {code}", { code: incident.code }),
        detail: t("Il rejoint la DIREX, prévu ; son heure d'injection suit son temps."),
        tone: "ok",
        action: kept ? { label: t("Voir"), run: () => select(kept) } : undefined,
      });
    });
  const keepAll = () =>
    run(() => api.keepProposals(exerciseId as string, opo), (next) =>
      notify({
        text: t("Incidents proposés retenus"),
        detail: plural(next.incidents.filter((i) => i.source === "annotation").length, t("incident de la carte dans la DIREX"), t("incidents de la carte dans la DIREX")),
        tone: "ok",
      }),
    );
  const offered = !trained && ((data?.proposals?.length ?? 0) > 0 || (data?.dismissed?.length ?? 0) > 0);
  const clockAction = (action: ClockAction, value?: number | boolean) =>
    run(() => api.direxClock(exerciseId as string, action, value, opo), (next) => {
      if (action === "auto_inject")
        notify({
          text: next.clock.auto_inject ? t("Injection automatique activée") : t("Injection automatique suspendue"),
          detail: next.clock.auto_inject
            ? t("Chaque incident prévu part à son heure quand l'horloge tourne.")
            : t("Les incidents ne partent plus qu'à la main."),
          tone: "info",
        });
    });
  const sideTabs = [
    { value: "incident" as const, label: t("Incident") },
    { value: "croquis" as const, label: t("Croquis") },
    { value: "journal" as const, label: t("Journal"), count: data && data.events.length > 0 ? data.events.length : undefined },
  ];
  const sideTitle = side === "incident" ? (current ? current.code : t("Incident")) : side === "croquis" ? t("Croquis") : t("Journal");

  return (
    <div className="absolute inset-0 z-20 flex flex-col bg-canvas">
      <ViewHeader
        view="timeline"
        context={exercise?.name}
        badges={
          data && (
            <Segmented
              role="tab"
              size="sm"
              fill={false}
              ariaLabel={t("Affichage de la DIREX")}
              value={mode}
              onChange={setMode}
              options={[
                { value: "incidents", label: t("Incidents") },
                { value: "situation", label: t("Point de situation") },
              ]}
            />
          )
        }
        actions={
          <>
            {centac && opo && <OpoSelector value={opo} onChange={setOpo} />}
            {data && animator && (
              <>
                <GenerateMenu
                  open={generateOpen}
                  onOpenChange={setGenerateOpen}
                  compact={!wide}
                  highlight={incidents.length === 0}
                  cellules={data.cellules}
                  context={data.context}
                  busy={busy}
                  onGenerate={(body) =>
                    run(() => api.generateMelMil(exerciseId as string, body, opo), (next) => {
                      setGenerateOpen(false);
                      notify({
                        text: t("Incidents générés et posés"),
                        detail: plural(next.incidents.filter((i) => i.auto).length, t("incident généré"), t("incidents générés")),
                        tone: "ok",
                      });
                    })
                  }
                />
                <Button size="sm" onClick={() => setCreating(true)} disabled={busy} title={t("Nouvel incident")} aria-label={t("Nouvel incident")}>
                  <Plus size={14} /> {wide && t("Nouvel incident")}
                </Button>
              </>
            )}
            {data && <RoleMenu data={data} busy={busy} onChoose={chooseRole} />}
          </>
        }
        panelToggle={
          data &&
          mode === "incidents" && (
            <IconButton
              size="sm"
              onClick={() => setSideOpen((o) => !o)}
              active={sideOpen}
              title={sideOpen ? t("Masquer le volet") : t("Incident, croquis et journal")}
              aria-label={sideOpen ? t("Masquer le volet") : t("Incident, croquis et journal")}
            >
              {sideOpen ? <PanelRightClose size={16} /> : <PanelRightOpen size={16} />}
            </IconButton>
          )
        }
      />

      {!exerciseId || !complete || query.isError || !data ? (
        <div className="min-h-0 flex-1 overflow-y-auto">
          {!exerciseId ? (
            <DossierEmptyState view="timeline" kind="none">
              {t("La DIREX anime l'exercice d'un dossier : chronologie, incidents MEL/MIL, horloge et journal.")}
            </DossierEmptyState>
          ) : !complete ? (
            <DossierEmptyState view="timeline" kind="incomplete">
              {t("La DIREX anime l'exercice d'un dossier : chronologie, incidents MEL/MIL, horloge et journal.")}
            </DossierEmptyState>
          ) : query.isError ? (
            zoneRefusalOf(query.error) ? (
              <ZoneWaitState message={query.error.message} error={query.error} />
            ) : (
              <EmptyState variant="error">{query.error.message}</EmptyState>
            )
          ) : (
            <EmptyState variant="loading" title={t("Chargement de la DIREX…")} />
          )}
        </div>
      ) : (
        <>
          {/* Comptes nominatifs (docs/17 §3.7, §8.3) : DIREX d'un dossier qui n'est partagé à personne. */}
          <PrivateDirexBanner />
          <Timeline data={data} now={now} counts={counts} clock={<Clock data={data} now={now} busy={busy} onAction={clockAction} />} />
          <Presence show={unsaved && animator}>
            {unsaved && animator && (
              <Motion origin="top">
                <SaveDossierHint variant="banner" action="appliquer vos dernières modifications à la DIREX" />
              </Motion>
            )}
          </Presence>
          <Presence show={Boolean(me && me.role !== "animateur")}>
            {me && me.role !== "animateur" && (
              <Banner tone="info" icon={trained ? Users : Eye}>
                {trained ? (
                  <Bold
                    text={t(
                      "Poste en <b>cellule {cellule}</b> : vous voyez les incidents à mesure que la DIREX les injecte et marquez « traité » ceux de votre cellule. Les incidents à venir restent cachés.",
                      { cellule: term(me.cellule) },
                    )}
                  />
                ) : (
                  <Bold
                    text={t("Poste <b>observateur</b> : vous suivez tout l'exercice, incidents à venir compris, sans rien modifier.")}
                  />
                )}
              </Banner>
            )}
          </Presence>
          {mode === "situation" ? (
            <div key="situation" className="min-h-0 flex-1 overflow-y-auto">
              <SituationPanel exerciseId={exerciseId} opo={opo} />
            </div>
          ) : (
            <div className="relative flex min-h-0 flex-1">
              <main className="min-h-0 flex-1 overflow-y-auto">
                <div className="space-y-4 px-5 py-4 max-sm:px-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <Segmented
                      fill={false}
                      ariaLabel={t("Type d'incident")}
                      value={kind}
                      onChange={setKind}
                      options={(["all", "TAC", "TEC", "ENV"] as const).map((k) => ({
                        value: k,
                        label: k === "all" ? t("Tous") : t(KIND_LABEL[k]),
                        dot: k === "all" ? undefined : KIND_COLOR[k].fill,
                      }))}
                    />
                    {/* Largeur portée par un conteneur : la classe de champ est pleine largeur. */}
                    <div className="w-[150px] shrink-0">
                      <Select className={inputClass} value={cellule}
                        onChange={(e) => setCellule(e.target.value)} aria-label={t("Cellule")}>
                        <option value="">{t("Toutes les cellules")}</option>
                        {data.cellules.map((c) => (
                          <option key={c} value={c}>{term(c)}</option>
                        ))}
                      </Select>
                    </div>
                    <div className="w-[150px] shrink-0">
                      <Select className={inputClass} value={status}
                        onChange={(e) => setStatus(e.target.value as DirexStatus | "all")} aria-label={t("Statut")}>
                        <option value="all">{t("Tous les statuts")}</option>
                        {(trained ? (["injecte", "traite"] as const) : (["prevu", "injecte", "traite"] as const)).map((s) => (
                          <option key={s} value={s}>{t(STATUS_PLURAL[s])}</option>
                        ))}
                      </Select>
                    </div>
                    <SearchField value={search} onChange={setSearch} placeholder={t("Rechercher un incident")} className="min-w-[180px] flex-1" />
                    <span className="text-meta text-ink-muted tabular-nums">
                      {visible.length !== incidents.length
                        ? t("{shown} sur {total}", { shown: plural(visible.length, t("incident"), t("incidents")), total: incidents.length })
                        : plural(visible.length, t("incident"), t("incidents"))}
                    </span>
                    {data.next && (
                      <Button
                        variant="secondary"
                        onClick={() => data.next && select(data.next)}
                        title={
                          data.clock.auto_inject
                            ? t("Prochain incident injecté automatiquement, à son heure")
                            : t("Prochain incident prévu (injection automatique suspendue : à injecter à la main)")
                        }
                      >
                        {data.clock.auto_inject ? <Zap size={14} className="text-accent" /> : <Clock3 size={14} className="text-ink-muted" />}
                        {t("Prochain :")} <strong className="font-semibold">{data.next.code}</strong>
                        {data.next.planned_dtg && <span className="font-mono text-ink-soft tabular-nums">{data.next.planned_dtg.split(" ")[0]}</span>}
                      </Button>
                    )}
                  </div>

                  <Presence show={offered}>
                    {offered && (
                      <Motion origin="top">
                        <ProposalsBand data={data} busy={busy} language={language} onDecide={decide} onKeepAll={keepAll} />
                      </Motion>
                    )}
                  </Presence>

                  {incidents.length === 0 ? (
                    <EmptyState compact icon={CalendarClock}
                      title={trained ? t("Aucun incident injecté pour l'instant") : t("Aucun incident dans la DIREX")}>
                      {trained
                        ? t("Les incidents apparaissent ici dès que la DIREX les injecte ; un avis signale ceux de votre cellule.")
                        : animator
                          ? t(
                              "« Générer MEL/MIL » en propose {n} du catalogue, classés selon la situation du dossier et posés sur le terrain ; « Nouvel incident » en ajoute un rédigé à la main.",
                              { n: data.catalog },
                            )
                          : t("L'animateur génère la MEL/MIL ; elle s'affichera ici.")}
                    </EmptyState>
                  ) : (
                    <div className="overflow-x-auto rounded-xl border border-line bg-surface shadow-card">
                      {/* Largeur minimale à la mesure de la place laissée par le volet (1 440 px, volet ouvert : 700 px) :
                          les trois temps tiennent sans être coupés au bord. */}
                      <table className="w-full min-w-[620px] table-fixed">
                        <thead>
                          <tr className="border-b border-line bg-canvas/60 text-left">
                            <SectionLabel as="th" inset className="w-[160px] px-3 py-2">{t("Cible")}</SectionLabel>
                            {TEMPOS.map((t) => {
                              const phase = data.phases.find((p) => p.phase === t);
                              return (
                                <th key={t} className="px-3 py-2">
                                  <span className="font-mono text-ui font-semibold text-ink">{t}</span>
                                  {phase?.start_dtg && <span className="ml-2 font-mono text-meta font-normal text-ink-muted tabular-nums">{phase.start_dtg}</span>}
                                </th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-line">
                          {rows.map((row) => (
                            <tr key={row.key} className="align-top">
                              <td className="px-3 py-2">
                                <span className={clsx("block text-ui leading-snug break-words", row.key === UNASSIGNED ? "text-ink-muted italic" : "font-semibold text-friend")}>
                                  {row.key === UNASSIGNED ? row.label : unitDesignation(row.label)}
                                </span>
                              </td>
                              {TEMPOS.map((t) => {
                                const cell = visible.filter((i) => i.tempo === t && (i.unit_key ?? UNASSIGNED) === row.key);
                                return (
                                  <td key={t} className="px-2 py-1.5">
                                    <div className="space-y-1">
                                      {cell.map((i) => (
                                        <IncidentChip key={i.id} incident={i} selected={i.id === selected} own={trained && i.cellule === me?.cellule}
                                          language={language} onClick={() => select(i)} />
                                      ))}
                                    </div>
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </main>

              {/* Volet Incident, Croquis, Journal : volet latéral partagé, en ligne à partir de 1280 px, en
                  surimpression avec voile en dessous (fermeture au toucher du voile et par Échap). */}
              <Presence show={sideOpen}>
                {sideOpen && (
                  <SidePanel
                    title={sideTitle}
                    icon={CalendarClock}
                    onClose={() => setSideOpen(false)}
                    tabs={<Tabs ariaLabel={t("Incident, croquis et journal")} tabs={sideTabs} value={side} onChange={setSide} />}
                  >
                    {side === "incident" &&
                      (current ? (
                        <IncidentPanel
                          key={current.id}
                          incident={current}
                          data={data}
                          busy={busy}
                          edit={incidentDraft?.id === current.id ? incidentDraft : null}
                          onEdit={setIncidentDraft}
                          onChange={change}
                          onDelete={() => run(() => api.deleteIncident(exerciseId as string, current.id, opo), () => setSelected(null))}
                          onLocate={() => {
                            setSketchPhase(current.tempo);
                            setSide("croquis");
                          }}
                        />
                      ) : (
                        <PanelNote className="text-center">{t("Choisissez un incident dans le tableau.")}</PanelNote>
                      ))}
                    {side === "croquis" && (
                      <div key="croquis" className={motion("fade")}>
                        <div className="flex items-center justify-between gap-2 px-4 py-2">
                          <span className="text-meta text-ink-muted">{t("Amis et incidents du temps")}</span>
                          <Segmented
                            size="sm"
                            fill={false}
                            ariaLabel={t("Temps du croquis")}
                            value={sketchPhase}
                            onChange={setSketchPhase}
                            options={TEMPOS.map((p) => ({ value: p, label: p }))}
                          />
                        </div>
                        {data.sketch ? (
                          <>
                            <DirexSketch sketch={data.sketch} incidents={visible} phase={sketchPhase} selected={selected}
                              onSelect={(id) => {
                                const i = incidents.find((x) => x.id === id);
                                if (i) select(i);
                              }} />
                            <SourcesNote text={data.sketch.attribution} />
                          </>
                        ) : (
                          <PanelNote className="text-center">{t("Aucune unité placée pour situer les incidents.")}</PanelNote>
                        )}
                        <p className="px-4 py-2.5 text-meta leading-snug text-ink-muted">
                          {t("Croquis de situation, nord en haut : fuseau, routes, localités, amis du temps et incidents posés (rouge tactique, gris technique, jaune environnement).")}
                        </p>
                      </div>
                    )}
                    {side === "journal" && (
                      <div key="journal" className={motion("fade")}>
                        {data.events.length === 0 ? (
                          <PanelNote className="text-center">
                            {t("Le journal retrace générations, injections, traitements et réglages de l'horloge, avec le poste qui a agi.")}
                          </PanelNote>
                        ) : (
                          <ul className="divide-y divide-line">
                            {data.events.map((e) => (
                              <li key={e.id} className="flex min-h-8 gap-3 px-4 py-2">
                                <History size={14} className="mt-0.5 shrink-0 text-ink-muted" />
                                <span className="min-w-0 flex-1">
                                  <span className="block text-ui leading-snug text-ink">{tServer(e.text)}</span>
                                  <span className="block text-meta text-ink-muted tabular-nums">
                                    {e.exercise_time ? formatDtg(e.exercise_time, 0, data.time_zone) : "—"}
                                    {" · "}
                                    {new Date(e.at).toLocaleTimeString(locale(), { hour: "2-digit", minute: "2-digit" })}
                                    {e.terminal ? ` · ${terminalName(e.terminal)}` : ""}
                                  </span>
                                </span>
                                {e.incident && <Badge>{e.incident}</Badge>}
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>
                    )}
                  </SidePanel>
                )}
              </Presence>
            </div>
          )}
        </>
      )}

      <Presence show={creating && Boolean(data)}>
        {creating && data && (
          <NewIncidentDialog
            data={data}
            language={language}
            tempo={(data.clock.phase as Phase) && TEMPOS.includes(data.clock.phase as Phase) ? (data.clock.phase as Phase) : "T1"}
            busy={busy}
            onClose={() => setCreating(false)}
            onCreate={(incident: NewIncident) =>
              run(() => api.createIncident(exerciseId as string, incident, opo), (next) => {
                setCreating(false);
                const created = [...next.incidents].reverse().find((i) => i.source === "custom" && i.short === incident.short);
                if (created) select(created);
              })
            }
          />
        )}
      </Presence>
    </div>
  );
}
